import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Phone, Trophy, Zap, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials, resolveAvatar } from "@/lib/api";

/** Toca fanfarra de INÍCIO de sprint (série ascendente de notas). */
function playSprintStart() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C-E-G-C
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.value = f;
      const t = now + i * 0.16;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.4);
    });
  } catch { /* noop */ }
}

/** Toca fanfarra de FIM de sprint (aplauso/trombeta descendente triunfante). */
function playSprintEnd() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const seq = [
      { f: 1046.5, t: 0.0 },
      { f: 1318.5, t: 0.15 },
      { f: 1567.98, t: 0.3 },
      { f: 2093.0, t: 0.45 },
      { f: 1567.98, t: 0.7 },
      { f: 2093.0, t: 0.9 },
    ];
    seq.forEach(({ f, t }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.value = f;
      const start = now + t;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.3, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
      o.connect(g).connect(ctx.destination);
      o.start(start);
      o.stop(start + 0.55);
    });
  } catch { /* noop */ }
}

function formatMMSS(secs) {
  if (secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Overlay global do Sprint de Ligações.
 * - Se sprint ativo: HUD flutuante no canto inferior-direito com countdown, ranking parcial e som de início.
 * - Ao encerrar: banner grande com vencedor + som de fim (5s).
 * - Silenciável via user.som_ativo.
 */
export default function SprintOverlay({ tvMode = false }) {
  const { lastEvent } = useRealtime() || {};
  const { user } = useAuth() || {};
  const [sprint, setSprint] = useState(null);
  const [ended, setEnded] = useState(null);
  const [now, setNow] = useState(Date.now());
  const startedSoundPlayedRef = useRef(new Set());
  const endedSoundPlayedRef = useRef(new Set());

  // Determina se o usuário atual deve visualizar o sprint (respeita target_vendedoras).
  // TV mode e diretoria sempre veem. Vendedoras/supervisão fora da lista de alvos são ignoradas.
  const isRelevant = useCallback((s) => {
    if (!s) return false;
    if (tvMode) return true;
    const targets = s.target_vendedoras;
    if (!targets || targets.length === 0) return true; // sem alvo = todas
    if (user?.role === "diretoria") return true;
    return targets.includes(user?.id);
  }, [tvMode, user]);

  const fetchActive = useCallback(async () => {
    try {
      const url = tvMode ? `${process.env.REACT_APP_BACKEND_URL}/api/sprints/ativo` : null;
      let data = null;
      if (tvMode) {
        // TV: sem auth (endpoint requer auth), então só puxamos via WS. Silencioso.
        return;
      }
      const r = await api.get("/sprints/ativo");
      data = r.data;
      if (data && data.status === "ativo" && isRelevant(data)) {
        setSprint(data);
      } else if (data && data.status === "finalizado" && data.recente && isRelevant(data)) {
        setEnded(data);
      } else {
        setSprint(null);
      }
    } catch { /* noop */ }
  }, [tvMode, isRelevant]);

  // Carrega estado inicial
  useEffect(() => {
    if (!tvMode) fetchActive();
    const t = setInterval(() => { if (!tvMode) fetchActive(); }, 20000);
    return () => clearInterval(t);
  }, [fetchActive, tvMode]);

  // Ticker
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Atualiza ranking parcial a cada 15s enquanto ativo
  useEffect(() => {
    if (!sprint || sprint.status !== "ativo" || tvMode) return;
    const t = setInterval(() => {
      api.get("/sprints/ativo").then((r) => {
        if (r.data && r.data.status === "ativo") setSprint(r.data);
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [sprint, tvMode]);

  // Eventos WebSocket
  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === "sprint_started") {
      const s = lastEvent.sprint;
      if (!isRelevant(s)) return; // não é alvo deste sprint
      setSprint(s);
      setEnded(null);
      if (!startedSoundPlayedRef.current.has(s.id)) {
        startedSoundPlayedRef.current.add(s.id);
        if (tvMode || user?.som_ativo !== false) playSprintStart();
      }
    } else if (lastEvent.event === "sprint_ended") {
      const s = lastEvent.sprint;
      if (!isRelevant(s)) return;
      setEnded(s);
      setSprint(null);
      if (!endedSoundPlayedRef.current.has(s.id)) {
        endedSoundPlayedRef.current.add(s.id);
        if (tvMode || user?.som_ativo !== false) playSprintEnd();
      }
      // limpa banner de encerramento após 20s
      setTimeout(() => setEnded((cur) => (cur && cur.id === s.id ? null : cur)), 20000);
    }
  }, [lastEvent, user, tvMode, isRelevant]);

  const restantes = sprint
    ? Math.max(0, Math.floor((new Date(sprint.fim).getTime() - now) / 1000))
    : 0;

  // Auto-finaliza local após timer estourar (backend também finaliza no próximo GET)
  useEffect(() => {
    if (sprint && restantes === 0) {
      const t = setTimeout(() => { if (!tvMode) fetchActive(); }, 2000);
      return () => clearTimeout(t);
    }
  }, [sprint, restantes, fetchActive, tvMode]);

  // ------- Render -------
  // Banner de encerramento (vencedor)
  if (ended) {
    const g = ended.ganhador;
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none px-4" data-testid="sprint-ended">
        <div className="celebrate-anim bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-white/30 max-w-2xl w-full text-center pointer-events-auto">
          <div className="text-white/90 uppercase tracking-widest text-xs md:text-sm font-bold flex items-center justify-center gap-2">
            <Trophy className="w-5 h-5" /> Sprint de Ligações · Encerrado
          </div>
          <div className="mt-2 font-display text-3xl md:text-5xl font-extrabold text-white leading-none">
            {g ? "🏆 Vencedora" : "Sem ligações no sprint"}
          </div>
          {g && (
            <>
              <div className="mt-6 flex flex-col items-center gap-3">
                <Avatar className="w-24 h-24 ring-4 ring-white/50">
                  <AvatarImage src={resolveAvatar(g.avatar_url)} alt={g.nome} />
                  <AvatarFallback className="bg-white/20 text-white text-3xl font-bold">
                    {initials(g.nome)}
                  </AvatarFallback>
                </Avatar>
                <div className="font-display text-4xl md:text-6xl font-extrabold text-white">{g.nome}</div>
                <div className="font-display text-2xl md:text-3xl font-bold text-white/90">
                  {g.ligacoes} {g.ligacoes === 1 ? "ligação" : "ligações"}
                </div>
              </div>
              {ended.ranking_final && ended.ranking_final.length > 1 && (
                <div className="mt-6 text-white/90 text-sm space-y-1">
                  {ended.ranking_final.slice(1, 5).map((r, i) => (
                    <div key={r.vendedora_id} className="flex justify-between px-4">
                      <span>{i + 2}º · {r.nome}</span>
                      <span className="font-bold tabular-nums">{r.ligacoes}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {!tvMode && (
            <button
              onClick={() => setEnded(null)}
              className="mt-6 text-white/70 hover:text-white text-xs underline"
              data-testid="sprint-ended-close"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!sprint || sprint.status !== "ativo") return null;

  const ranking = sprint.ranking_parcial || [];

  // HUD flutuante
  if (tvMode) {
    return (
      <div className="fixed top-4 left-4 z-[9997] max-w-sm" data-testid="sprint-hud-tv">
        <div className="bg-gradient-to-br from-red-600 via-orange-500 to-amber-500 rounded-2xl shadow-2xl p-4 border-2 border-white/20">
          <div className="flex items-center gap-2 text-white uppercase text-xs font-bold tracking-widest">
            <Zap className="w-4 h-4" /> Sprint de Ligações
          </div>
          <div className="font-display text-5xl font-extrabold tabular-nums text-white text-center my-2">
            {formatMMSS(restantes)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9997] max-w-xs w-72" data-testid="sprint-hud">
      <div className="bg-gradient-to-br from-red-600 via-orange-500 to-amber-500 rounded-2xl shadow-2xl overflow-hidden border-2 border-white/20">
        <div className="px-4 py-2 flex items-center justify-between text-white">
          <div className="flex items-center gap-2 uppercase text-xs font-bold tracking-widest">
            <Zap className="w-4 h-4 animate-pulse" /> Sprint de Ligações
          </div>
          <div className="font-display text-2xl font-extrabold tabular-nums" data-testid="sprint-timer">
            {formatMMSS(restantes)}
          </div>
        </div>
        <div className="bg-white/95 px-3 py-2 text-slate-800 max-h-56 overflow-auto">
          <div className="text-[10px] uppercase text-slate-500 tracking-widest font-bold mb-1 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Ranking parcial · ligações
          </div>
          {ranking.length === 0 && (
            <div className="text-slate-400 text-xs py-2 text-center">Nenhuma ligação ainda. Bora!</div>
          )}
          {ranking.slice(0, 6).map((r, i) => (
            <div key={r.vendedora_id} className="flex items-center justify-between py-1 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[11px] font-bold w-4 ${i === 0 ? "text-amber-600" : "text-slate-500"}`}>{i + 1}º</span>
                <span className="truncate">{r.nome}</span>
              </div>
              <span className="font-bold tabular-nums text-slate-900">{r.ligacoes}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
